import {memo, ReactNode} from 'react';
import styled from 'styled-components';
import {Palette} from 'hubbl-shared';

interface CardStatusIndicatorProps {
  children: ReactNode;
}

export const CardStatusIndicator = memo(
  ({children}: CardStatusIndicatorProps) => {
    return <StatusWrapper>{children}</StatusWrapper>;
  },
);

interface StatusBadgeProps {
  status: 'attending' | 'declined' | 'none' | 'action-required';
  children: ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  isClickable?: boolean;
}

export const StatusBadge = memo(
  ({status, children, onClick, isClickable = false}: StatusBadgeProps) => {
    return (
      <Badge status={status} onClick={onClick} isClickable={isClickable}>
        {children}
      </Badge>
    );
  },
);

const StatusWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const Badge = styled.div<{
  status: 'attending' | 'declined' | 'none' | 'action-required';
  isClickable?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 32px;
  padding: 0 16px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 700;
  min-width: 100px;
  border: 2px solid transparent;
  transition: all 0.2s ease;
  cursor: ${props => (props.isClickable ? 'pointer' : 'default')};

  background: ${props => {
    switch (props.status) {
      case 'attending':
        return Palette.primaryPadua;
      case 'declined':
        return Palette.systemError;
      case 'action-required':
        return Palette.almostBlack;
      case 'none':
        return Palette.almostBlack;
      default:
        return Palette.almostBlackTransparent10;
    }
  }};

  color: ${props => {
    switch (props.status) {
      case 'attending':
        return Palette.white;
      case 'declined':
        return Palette.white;
      case 'action-required':
        return Palette.white;
      case 'none':
        return Palette.white;
      default:
        return Palette.almostBlack;
    }
  }};

  border-color: ${props => {
    switch (props.status) {
      case 'attending':
        return Palette.primaryPadua;
      case 'declined':
        return Palette.systemError;
      case 'action-required':
        return Palette.almostBlack;
      default:
        return 'transparent';
    }
  }};

  box-shadow: ${props => {
    switch (props.status) {
      case 'attending':
        return `0 2px 8px ${Palette.primaryPadua}40`;
      case 'declined':
        return `0 2px 8px ${Palette.systemError}40`;
      case 'action-required':
        return `0 2px 8px ${Palette.primaryBianca}40`;
      default:
        return 'none';
    }
  }};

  &:hover {
    transform: ${props => (props.isClickable ? 'translateY(-1px)' : 'none')};
    box-shadow: ${props => {
      if (!props.isClickable) return 'none';
      switch (props.status) {
        case 'attending':
          return `0 4px 12px ${Palette.primaryPadua}60`;
        case 'declined':
          return `0 4px 12px ${Palette.systemError}60`;
        case 'action-required':
          return `0 4px 12px ${Palette.primaryBianca}60`;
        default:
          return '0 2px 4px rgba(0, 0, 0, 0.1)';
      }
    }};
  }

  &:active {
    transform: ${props => (props.isClickable ? 'translateY(0)' : 'none')};
  }
`;
